import classNames from 'classnames';
import { useEffect, useRef } from 'react';

import { Button, ThankYou } from '@ctct/payflow-ui';
import { PartnershipOfferCard } from '@ctct/presentation-layer';

import type { ThankYouWidgetProps } from './useThankYouWidget';
import ctctLogo from '../../assets/ctct-logo.svg';
import xeroLogo from '../../assets/xero-logo.webp';

import './thank-you-widget.scss';

const RegisterSmsExternalLinkIcon = () => (
  <svg
    className="thank-you-widget__sms-bundle-external-icon"
    width={18}
    height={18}
    viewBox="0 0 24 24"
    aria-hidden
    focusable="false"
  >
    <path
      fill="currentColor"
      d="M19 19H5V5h4V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4h-2v4zM15 3v2h3.59l-9.83 9.83 1.41 1.41L20 6.41V10h2V3h-7z"
    />
  </svg>
);

export const ThankYouWidget = ({ data, handlers, externalHooks, version, flags }: ThankYouWidgetProps) => {
  const hasSentInitialEmail = useRef(false);
  const hasTrackedConversion = useRef(false);
  const trackConversionRef = useRef(handlers.trackConversion);

  trackConversionRef.current = handlers.trackConversion;

  // Automatically send verification email when data loads for social starter with unverified email
  useEffect(() => {
    if (
      !hasSentInitialEmail.current &&
      flags.isSocialStarter &&
      !flags.hasVerifiedEmail &&
      flags.billingEmailId !== undefined
    ) {
      hasSentInitialEmail.current = true;
      handlers.onResendVerificationClick();
    }
  }, [flags.isSocialStarter, flags.hasVerifiedEmail, flags.billingEmailId, handlers]);

  // Track conversion once when soid loads (fire-and-forget, best-effort)
  // useAccountId() is async, so initial render has undefined accountId — effect re-runs when soid arrives
  useEffect(() => {
    if (hasTrackedConversion.current || !trackConversionRef.current || !flags.soid) {
      return;
    }

    hasTrackedConversion.current = true;

    const trackFn = trackConversionRef.current;

    const runConversionTracking = async () => {
      const tracked = await trackFn();

      if (!tracked) {
        hasTrackedConversion.current = false;
      }
    };

    runConversionTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags.soid]);

  const returnUrl = externalHooks.useReturnUrl();
  const returnLabel = externalHooks.useReturnLabel();

  const isXeroAddonSelected = externalHooks.useIsXeroAddonSelected();
  const isMoretonBayVersion = version === 'moreton-bay';
  const isSmsBundleVersion = version === 'sms-bundle';

  const shouldShowXeroPartner = data.getShouldShowXeroPartner();
  const shouldShowResendVerification = data.getShouldShowResendVerification();

  const thankYouProps = {
    heading: data.getHeading(),
    subheading: data.getSubHeading(),
    subheading2: data.getSubHeading2(),
    buttonLabel: returnLabel || data.getButtonLabel(returnUrl),
    buttonOnClick: () => handlers.onMainButtonClick(returnUrl),
    buttonVariant: shouldShowResendVerification ? ('text' as const) : ('primary' as const),
    resendVerificationButton: shouldShowResendVerification
      ? {
          label: data.getResendVerificationButtonLabel(),
          onClick: handlers.onResendVerificationClick,
        }
      : undefined,
    card: data.getShouldShowCard()
      ? {
          title: data.getCardTitle(),
          description: data.getCardDescription(),
          buttonLabel: data.getCardButtonLabel(),

          onButtonClick: handlers.onCardButtonClick,
        }
      : undefined,
  };

  if (isSmsBundleVersion) {
    const c1 = data.getSmsBundleColumnOne();
    const c2 = data.getSmsBundleColumnTwo();

    return (
      <div className="thank-you-widget thank-you-widget--sms-bundle">
        <header className="thank-you-widget__sms-bundle-hero">
          <h1 className="thank-you-widget__sms-bundle-success-heading">{data.getHeading()}</h1>
          <p className="thank-you-widget__sms-bundle-success-sub">{data.getSubHeading()}</p>
        </header>
        <div className="thank-you-widget__sms-bundle-grid">
          <div className="thank-you-widget__sms-bundle-col">
            <h2 className="thank-you-widget__sms-bundle-heading">{c1.heading}</h2>
            <p className="thank-you-widget__sms-bundle-line">{c1.line}</p>
            <Button
              className="thank-you-widget__sms-bundle-register-btn"
              fullWidth
              size="large"
              shape="square"
              variant="primary"
              onClick={handlers.onRegisterSmsClick}
            >
              <span className="thank-you-widget__sms-bundle-register-btn-inner">
                {data.getSmsBundleRegisterSmsLabel()}
                <RegisterSmsExternalLinkIcon />
              </span>
            </Button>
            <div className="thank-you-widget__sms-bundle-next-box">
              <p className="thank-you-widget__sms-bundle-steps-heading">{c1.stepsHeading}</p>
              <ol className="thank-you-widget__sms-bundle-steps">
                <li>{c1.step1}</li>
                <li>{c1.step2}</li>
                <li>{c1.step3}</li>
              </ol>
            </div>
          </div>
          <div className="thank-you-widget__sms-bundle-col thank-you-widget__sms-bundle-col--secondary">
            <h2 className="thank-you-widget__sms-bundle-secondary-heading">{c2.heading}</h2>
            <p className="thank-you-widget__sms-bundle-secondary-line">{c2.line}</p>
            <Button
              fullWidth
              size="large"
              shape="square"
              variant="secondary"
              onClick={() => handlers.onMainButtonClick(returnUrl ?? null)}
            >
              {c2.goToDashboard}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (shouldShowXeroPartner) {
    let partnershipOfferContent = data.getXeroPartnerContent();

    if (isMoretonBayVersion) {
      partnershipOfferContent = data.getXeroPartnerContentForMoretonBay();
      thankYouProps.heading = data.getHeadingForMoretonBay();
      thankYouProps.subheading = data.getSubHeadingForMoretonBay();
    } else if (isXeroAddonSelected) {
      partnershipOfferContent = data.getXeroPartnerContentWithAddonSelected();
    }

    const lightBackground = isMoretonBayVersion || isXeroAddonSelected;

    return (
      <div className="thank-you-widget">
        <div className="thank-you-widget__thank-you">
          <ThankYou
            {...thankYouProps}
            variant="merged"
            buttonVariant={lightBackground ? 'secondary' : 'primary'}
            buttonSize={lightBackground ? 'medium' : 'large'}
          />
        </div>
        <div className="thank-you-widget__partnership-offer-card">
          <PartnershipOfferCard
            className={classNames(lightBackground && 'light-bg-version')}
            {...partnershipOfferContent}
            logos={[
              { id: 'ctct', logo: <img src={ctctLogo} alt="Constant Contact" /> },
              { id: 'xero', logo: <img src={xeroLogo} alt="Xero" /> },
            ]}
            btnOnClick={handlers.onXeroPartnerButtonClick}
            btnVariant={lightBackground ? 'primary' : 'white'}
          />
        </div>
      </div>
    );
  }

  return <ThankYou {...thankYouProps} />;
};
